/**
 * T077: Prompt Edit Page
 * Page for editing an existing prompt
 */

import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PromptForm, type PromptFormSubmitValues } from '@/components/prompts/prompt-form'
import { LoadingSpinner } from '@/components/common/loading-spinner'
import { ErrorMessage } from '@/components/common/error-message'
import { usePrompt } from '@/hooks/usePrompt'
import { useUpdatePrompt } from '@/hooks/useUpdatePrompt'
import { ApiError } from '@/services/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { UpdatePromptInput } from '@/services/prompts'

interface PromptEditPageProps {
  promptId: string
}

export function PromptEditPage({ promptId }: PromptEditPageProps) {
  const navigate = useNavigate()
  const { data: prompt, isLoading, error, refetch } = usePrompt(promptId)
  const updatePrompt = useUpdatePrompt()
  const [showConflictDialog, setShowConflictDialog] = useState(false)
  const [pendingValues, setPendingValues] = useState<UpdatePromptInput | null>(null)

  const handleSubmit = (values: PromptFormSubmitValues) => {
    const input: UpdatePromptInput = {
      title: values.title,
      content: values.content,
      category_id: values.category_id,
      tag_ids: values.tag_ids,
    }

    updatePrompt.mutate(
      { id: promptId, input },
      {
        onSuccess: () => {
          navigate({ to: '/prompts/$id', params: { id: promptId } })
        },
        onError: (err) => {
          // T082: Handle 409 conflict
          if (err instanceof ApiError && err.status === 409) {
            setPendingValues(input)
            setShowConflictDialog(true)
          }
        },
      }
    )
  }

  const handleForceUpdate = () => {
    if (pendingValues) {
      // Force update by re-fetching and re-submitting
      refetch().then(() => {
        updatePrompt.mutate(
          { id: promptId, input: pendingValues },
          {
            onSuccess: () => {
              setShowConflictDialog(false)
              setPendingValues(null)
              navigate({ to: '/prompts/$id', params: { id: promptId } })
            },
          }
        )
      })
    }
  }

  const handleReload = () => {
    setShowConflictDialog(false)
    setPendingValues(null)
    refetch()
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage
        title="Failed to load prompt"
        message={error.message}
        onRetry={refetch}
      />
    )
  }

  if (!prompt) {
    return (
      <ErrorMessage
        title="Prompt not found"
        message="The prompt you're looking for doesn't exist or has been deleted."
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/prompts/$id" params={{ id: promptId }}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit Prompt</h1>
      </div>

      <PromptForm
        defaultValues={{
          title: prompt.title,
          content: prompt.content,
          category_id: prompt.category_id,
          tag_ids: prompt.tags?.map((t) => t.id) ?? [],
          config: prompt.config,
          metadata: prompt.metadata,
        }}
        onSubmit={handleSubmit}
        isSubmitting={updatePrompt.isPending}
        submitLabel="Save Changes"
      />

      {updatePrompt.error && !(updatePrompt.error instanceof ApiError && updatePrompt.error.status === 409) && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {updatePrompt.error.message}
        </div>
      )}

      {/* T082: Conflict resolution dialog */}
      <Dialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Conflict</DialogTitle>
            <DialogDescription>
              This prompt has been modified by another user or in another tab. What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleReload}>
              Reload Latest
            </Button>
            <Button variant="destructive" onClick={handleForceUpdate}>
              Overwrite Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
